import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import deliveryFleetService from '../../services/deliveryFleetService';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function DriverDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resetCreds, setResetCreds] = useState(null);
  const [viewingAgreement, setViewingAgreement] = useState(false);

  const load = () => {
    setLoading(true);
    deliveryFleetService.getDriver(id).then(({ driver: d, deliveries: dels }) => {
      setDriver(d);
      setDeliveries(dels);
      setForm({
        firstName: d.firstName, lastName: d.lastName, phone: d.phone || '', email: d.email || '',
        dateOfBirth: d.dateOfBirth || '', status: d.status,
        vehicleMake: d.vehicleMake || '', vehicleModel: d.vehicleModel || '', vehicleColor: d.vehicleColor || '', vehiclePlate: d.vehiclePlate || '',
        driverLicenseNumber: d.driverLicenseNumber || '', driverLicenseExpiry: d.driverLicenseExpiry || '',
        insuranceProvider: d.insuranceProvider || '', insurancePolicyNumber: d.insurancePolicyNumber || '', insuranceExpiry: d.insuranceExpiry || '',
        zelleHandle: d.zelleHandle || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const updated = await deliveryFleetService.updateDriver(id, form);
      setDriver(d => ({ ...d, ...updated }));
    } catch {
      setError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    const creds = await deliveryFleetService.resetDriverPassword(id);
    setResetCreds(creds);
  };

  const handleViewAgreement = async () => {
    setViewingAgreement(true);
    try {
      const url = await deliveryFleetService.getDriverAgreementPdf(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Failed to open the signed agreement.');
    } finally {
      setViewingAgreement(false);
    }
  };

  if (loading || !form) return <div className="text-center py-5"><span className="spinner-border" /></div>;

  return (
    <div className="container mt-4" style={{ maxWidth: 900 }}>
      <button className="btn btn-link p-0 text-muted mb-2" onClick={() => navigate('/delivery-fleet/drivers')}>← Drivers</button>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{driver.firstName} {driver.lastName}</h4>
        <div className="d-flex gap-2">
          {driver.agreementSignedAt ? (
            <button className="btn btn-success btn-sm" onClick={handleViewAgreement} disabled={viewingAgreement}>
              {viewingAgreement ? 'Opening…' : '📄 Agreement Signed — View'}
            </button>
          ) : <span className="badge bg-warning text-dark">Agreement Not Signed</span>}
          <button className="btn btn-outline-secondary btn-sm" onClick={handleResetPassword}>Reset Portal Password</button>
        </div>
      </div>

      {resetCreds && (
        <div className="alert alert-success">
          New temp password for badge <strong>{resetCreds.badgeNumber}</strong>: <strong>{resetCreds.tempPassword}</strong>
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card text-center"><div className="card-body py-3">
            <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Deliveries</div>
            <div className="fw-bold" style={{ fontSize: 20 }}>{driver.earnings.deliveriesCompleted}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center"><div className="card-body py-3">
            <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Earned</div>
            <div className="fw-bold text-success" style={{ fontSize: 20 }}>{money(driver.earnings.totalEarnedUsd)}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center"><div className="card-body py-3">
            <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Owed</div>
            <div className="fw-bold" style={{ fontSize: 20, color: driver.earnings.totalOwedUsd > 0 ? '#dc3545' : undefined }}>{money(driver.earnings.totalOwedUsd)}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center"><div className="card-body py-3">
            <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Avg Delivery</div>
            <div className="fw-bold" style={{ fontSize: 20 }}>{driver.earnings.avgDeliveryMinutes != null ? `${driver.earnings.avgDeliveryMinutes}m` : '—'}</div>
          </div></div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>Profile</div>
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          <div className="row g-2 mb-2">
            <div className="col-6"><label className="form-label small mb-1">First Name</label><input className="form-control form-control-sm" value={form.firstName} onChange={e => set('firstName', e.target.value)} /></div>
            <div className="col-6"><label className="form-label small mb-1">Last Name</label><input className="form-control form-control-sm" value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-4"><label className="form-label small mb-1">Phone</label><input className="form-control form-control-sm" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
            <div className="col-4"><label className="form-label small mb-1">Email</label><input className="form-control form-control-sm" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div className="col-4"><label className="form-label small mb-1">Date of Birth</label><input type="date" className="form-control form-control-sm" value={form.dateOfBirth || ''} onChange={e => set('dateOfBirth', e.target.value)} /></div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-4"><label className="form-label small mb-1">Vehicle Make</label><input className="form-control form-control-sm" value={form.vehicleMake} onChange={e => set('vehicleMake', e.target.value)} /></div>
            <div className="col-4"><label className="form-label small mb-1">Vehicle Model</label><input className="form-control form-control-sm" value={form.vehicleModel} onChange={e => set('vehicleModel', e.target.value)} /></div>
            <div className="col-2"><label className="form-label small mb-1">Color</label><input className="form-control form-control-sm" value={form.vehicleColor} onChange={e => set('vehicleColor', e.target.value)} /></div>
            <div className="col-2"><label className="form-label small mb-1">Plate</label><input className="form-control form-control-sm" value={form.vehiclePlate} onChange={e => set('vehiclePlate', e.target.value)} /></div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-6"><label className="form-label small mb-1">License #</label><input className="form-control form-control-sm" value={form.driverLicenseNumber} onChange={e => set('driverLicenseNumber', e.target.value)} /></div>
            <div className="col-6"><label className="form-label small mb-1">License Expiry</label><input type="date" className="form-control form-control-sm" value={form.driverLicenseExpiry || ''} onChange={e => set('driverLicenseExpiry', e.target.value)} /></div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-4"><label className="form-label small mb-1">Insurance Provider</label><input className="form-control form-control-sm" value={form.insuranceProvider} onChange={e => set('insuranceProvider', e.target.value)} /></div>
            <div className="col-4"><label className="form-label small mb-1">Policy #</label><input className="form-control form-control-sm" value={form.insurancePolicyNumber} onChange={e => set('insurancePolicyNumber', e.target.value)} /></div>
            <div className="col-4"><label className="form-label small mb-1">Insurance Expiry</label><input type="date" className="form-control form-control-sm" value={form.insuranceExpiry || ''} onChange={e => set('insuranceExpiry', e.target.value)} /></div>
          </div>
          <div className="row g-2 mb-3">
            <div className="col-6"><label className="form-label small mb-1">Zelle Handle</label><input className="form-control form-control-sm" value={form.zelleHandle} onChange={e => set('zelleHandle', e.target.value)} /></div>
            <div className="col-6">
              <label className="form-label small mb-1">Status</label>
              <select className="form-select form-select-sm" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>Delivery History</div>
        <div className="card-body p-0">
          {deliveries.length === 0 ? (
            <p className="text-muted fst-italic p-3 mb-0">No deliveries yet.</p>
          ) : (
            <table className="table table-sm mb-0">
              <thead className="table-light"><tr><th>Order</th><th className="text-end">Payout</th><th>Status</th><th>Paid</th></tr></thead>
              <tbody>
                {deliveries.map(d => (
                  <tr key={d.id}>
                    <td className="small">#{d.Order?.orderNumber}</td>
                    <td className="text-end">{money(d.payoutAmountUsd)}</td>
                    <td><span className="badge bg-secondary text-capitalize">{d.status.replace('_', ' ')}</span></td>
                    <td>{d.payoutStatus === 'paid' ? <span className="badge bg-success">Paid</span> : <span className="text-muted small">Pending</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
